// Blueprint: forge/blueprint/frontend/architecture/001-shell-composition.md
import { inject, injectable, Store } from '@servicetitan/react-ioc';
import { makeObservable, observable, computed, runInAction } from 'mobx';
import { ModulesApi, NavSectionsApi, type ModuleDto, type NavSectionDto } from 'org-hub-api';

import { readOrgHubNotEnabled } from '../api/org-hub-not-enabled';
import { RootOrgIdToken } from '../tokens';

/** A module that mounts as a top-level route: `loadType === 'route'` with a non-null `route` path. */
export type RouteModule = ModuleDto & { route: string };

/**
 * Shell sidebar view model: a navigation section resolved to its enabled module links. Derived in
 * `AppStore.sections` from the generated `NavSectionDto` + `ModuleDto` — intentionally a Shell-local shape,
 * not the API contract.
 */
export interface NavSection {
    id: string;
    title: string;
    items: NavItem[];
}

export interface NavItem {
    label: string;
    path: string;
    icon?: string;
}

@injectable()
export class AppStore extends Store {
    // Raw registry read-surface (org-hub-api contract). `sections` is the derived Shell view model.
    @observable.ref navSections: NavSectionDto[] = [];
    @observable.ref modules: ModuleDto[] = [];
    /*
     * Set to true when reads are skipped (no root org id) or a read returns non-2xx — renders an
     * error/empty state rather than an empty registry (fail-closed, matches server posture).
     */
    @observable hasError = false;
    /*
     * Set (with hasError) only when a read is the access gate's `OrgHubNotEnabled` 403: the caller's org does
     * not have Org Hub, so OrgHub shows a no-access message instead of an empty dashboard. Other 403s don't set it.
     */
    @observable notEnabled = false;
    // The caller's root org name from that 403 (ProblemDetails `orgName`), or null when not reported.
    @observable deniedOrgName: string | null = null;

    /*
     * The generated registry clients are provided by the host (createApiProviders), already configured for
     * its API base + auth cookie; the store just calls them.
     */
    constructor(
        @inject(NavSectionsApi) private readonly navSectionsApi: NavSectionsApi,
        @inject(ModulesApi) private readonly modulesApi: ModulesApi,
        @inject(RootOrgIdToken) private readonly rootOrgId: string | null
    ) {
        super();
        makeObservable(this);
    }

    /*
     * `initialize` is the react-ioc Store lifecycle hook: the Provider calls it on mount and renders its
     * `loadingFallback` until the returned promise settles. When `rootOrgId` is null or invalid the store
     * skips all reads and enters error state — no guessing with a bad org id. A non-2xx read also enters
     * error state without retry (fail-closed). Reads carry the `{rootOrgId}` path segment; the shared
     * Configuration supplies the `X-Root-Org-Id` header so every request is org-scoped.
     */
    initialize = async (): Promise<void> => {
        if (!this.rootOrgId || !isValidGuid(this.rootOrgId)) {
            runInAction(() => {
                this.hasError = true;
            });
            return;
        }

        const rootOrgId = this.rootOrgId;

        try {
            const [navSections, modules] = await Promise.all([
                this.navSectionsApi.getNavSections({ rootOrgId }).then(res => res.data),
                this.modulesApi.getModules({ rootOrgId }).then(res => res.data),
            ]);

            runInAction(() => {
                this.navSections = navSections;
                this.modules = modules;
            });
        } catch (error: unknown) {
            const notEnabled = readOrgHubNotEnabled(error);
            runInAction(() => {
                this.hasError = true;
                this.notEnabled = notEnabled !== null;
                this.deniedOrgName = notEnabled?.orgName ?? null;
            });
        }
    };

    /*
     * Enabled modules in nav order — the subset of the registry the Shell actually surfaces. Derived once
     * here so both the sidebar (`sections`) and direct consumers share the same filtered, ordered list.
     */
    @computed get enabledModules(): ModuleDto[] {
        return this.modules
            .filter(m => m.enabled)
            .sort((a, b) => (a.navOrder ?? 0) - (b.navOrder ?? 0));
    }

    /*
     * Enabled modules that mount as a top-level route (`loadType === 'route'` with a `route` path). These
     * are the modules the Shell registers in its router; non-route load types (e.g. embedded widgets) are
     * surfaced elsewhere, not as pages.
     */
    @computed get routeModules(): RouteModule[] {
        return this.enabledModules.filter(
            (m): m is RouteModule => m.loadType === 'route' && !!m.route
        );
    }

    /*
     * Landing route: the path the Shell redirects `/` to instead of a standalone dashboard. Mirrors the
     * sidebar exactly — the first navigable item (non-empty path) of the first nav section (in display
     * order) that has one. `undefined` when the registry surfaces nothing navigable (empty registry / all
     * disabled), in which case the Shell falls back to its placeholder home page.
     */
    @computed get landingPath(): string | undefined {
        for (const section of this.sections) {
            const item = section.items.find(i => i.path);
            if (item) {
                return item.path;
            }
        }
        return undefined;
    }

    /*
     * Shell sidebar view model: nav sections (ordered) each resolved to their enabled module links. Mirrors
     * the backend read surface (GUM-1923) — kept Shell-local, never the API contract shape.
     *
     * Sections with no visible module are omitted (GUM-3472): `GET nav-sections` returns the whole taxonomy
     * regardless of the caller, while `GET /modules` is filtered per caller (permission gate, `enabled_url`,
     * and gated modules hidden when the permission lookup fails) and may not be populated yet (the seed
     * leaves some sections empty for other teams' registrations). The Shell cannot tell those cases apart
     * and should render none of them as a bare header.
     */
    @computed get sections(): NavSection[] {
        return this.navSections
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(s => ({
                id: s.id,
                title: s.displayName,
                items: this.enabledModules
                    .filter(m => m.navSectionId === s.id)
                    .map(m => ({
                        label: m.displayName,
                        path: m.route ?? '',
                        icon: s.icon ?? undefined,
                    })),
            }))
            .filter(s => s.items.length > 0);
    }
}

/*
 * Accepts a Guid in the canonical xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx form (case-insensitive).
 * A malformed value is treated as absent — the store enters error state rather than sending a
 * syntactically-invalid id to the server (matches server fail-closed posture). Pure function so it
 * is trivially testable without standing up the store.
 */
export function isValidGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
